import React, { useCallback, useEffect, useRef } from "react";
import { FormProvider, UseFormReturn } from "react-hook-form";
import { trackPromise } from "react-promise-tracker";
import { Subject } from "rxjs";
import _ from "lodash";
import { FatOutlineButton } from "common/component/FatOutlineButton";
import { LoadingSpinner } from "common";
import {
  AuthReviewNeededInput
} from "../../domain";
import { ModalDatePicker } from "../../form-components/ModalDatePicker";
import { ModalTextAreaInput } from "../../form-components/ModalTextAreaInput";
import AuthReviewRequestReasonsInput from "./AuthReviewRequestReasonsInput";
import { parseISO } from "date-fns";
import { splitAndRemoveEmpty } from "placement/const";

type Props = {
  visible$: Subject<boolean>;
  methods: UseFormReturn<AuthReviewNeededInput>;
  onFinished: (formValues: AuthReviewNeededInput | null) => Promise<void>;
  onCancel: () => Promise<void>;
  externalLoading: boolean;
};

export function AuthReviewNeededForm({
  visible$,
  onFinished,
  methods,
  onCancel,
  externalLoading,
}: Props) {
  const submitWithTracking = useCallback(
    (formValues: AuthReviewNeededInput | null) =>
      trackPromise(onFinished(formValues)),
    [onFinished],
  );

  const { watch, setValue } = methods;
  const reasons = watch("authorizationReviewReasons");
  const authReviewFromDate = watch("authReviewFromDate");
  const authReviewToDate = watch("authReviewToDate");
  const reasonList = splitAndRemoveEmpty(reasons);
  const isRequestingAuthorization = reasonList.includes("RequestingAuthorizationExtension");
  const isAuthReviewDateRequired = !reasonList.includes("CheckingOnStatusOfAuthorization");

  // Clear dependent fields only when extension request changes from yes to no (true to false)
  const prevIsRequestingAuthorization = useRef(isRequestingAuthorization);

  useEffect(() => {
    if (prevIsRequestingAuthorization.current && !isRequestingAuthorization) {
      setValue("requestedExtensionDate", null);
      setValue("rationaleForDelay", "");
      setValue("firstScheduledAssessment", null);
    }
    prevIsRequestingAuthorization.current = isRequestingAuthorization;
  }, [setValue, isRequestingAuthorization]);

  const submit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      await methods.handleSubmit(async (data: AuthReviewNeededInput) => {
        await submitWithTracking(
          _.pick(data, [
            "authReviewFromDate",
            "authReviewToDate",
            "requestedExtensionDate",
            "rationaleForDelay",
            "firstScheduledAssessment",
            "authorizationReviewReasons",
            "comments",
          ]),
        );
        methods.reset();
        visible$.next(false);
      })(e);
    },
    [methods, visible$, submitWithTracking],
  );

  return (
    <FormProvider {...methods}>
      <form autoComplete="off" onSubmit={submit}>
        {!externalLoading && (
          <>
            <div className="space-y-6">
              <div
                className="flex flex-col w-full p-4 scrollbar-always"
                style={{
                  maxHeight: "calc(100vh - 68px - 100px - 37px - 90px - 60px)",
                }}
              >
                <>
                    <AuthReviewRequestReasonsInput
                      sending={externalLoading}
                      required={true}
                      fieldName="authorizationReviewReasons"
                      label="Authorization Review Reasons"
                    />

                    <div className="text-base font-semibold mb-4">
                      What authorization dates are you inquiring about?
                    </div>
                    <div className="flex flex-row gap-4 mb-2">
                        <div className="flex-1">
                            <ModalDatePicker<AuthReviewNeededInput>
                            labelPosition="top"
                            label={"Auth Review From Date"}
                            disabled={externalLoading}
                            placeholder="mm/dd/yyyy"
                            field={"authReviewFromDate"}
                            required={isAuthReviewDateRequired}
                            dependentFields={["authReviewToDate"]}
                            containerClassName="w-full"
                            labelClassName="block mb-2"
                            datePickerWrapperClassName="w-full"
                            optionalRules={{
                                validate: {
                                required: (value: string) => {
                                  if (isAuthReviewDateRequired) {
                                    if (!value || value.trim() === '') {
                                        return 'Required';
                                    }
                                  }
                                    return true;
                                },
                                dateRange: (value: string) => {
                                    if (!value || !authReviewToDate) return true;
                                    try {
                                    return (
                                        parseISO(value) <= parseISO(authReviewToDate) ||
                                        '"From Date" cannot be after "To Date"'
                                    );
                                    } catch {
                                    return "Invalid date format";
                                    }
                                },
                                },
                            }}
                            />
                        </div>

                        <div className="flex-1">
                            <ModalDatePicker<AuthReviewNeededInput>
                            labelPosition="top"
                            label={"Auth Review To Date"}
                            disabled={externalLoading}
                            placeholder="mm/dd/yyyy"
                            field={"authReviewToDate"}
                            required={isAuthReviewDateRequired}
                            dependentFields={["authReviewFromDate"]}
                            containerClassName="w-full"
                            labelClassName="block mb-2"
                            datePickerWrapperClassName="w-full"
                            optionalRules={{
                                validate: {
                                required: (value: string) => {
                                    if (isAuthReviewDateRequired) {
                                        if (!value || value.trim() === '') {
                                            return 'Required';
                                        }
                                    }
                                    return true;
                                },
                                dateRange: (value: string) => {
                                    if (!value || !authReviewFromDate) return true;
                                    try {
                                    return (
                                        parseISO(value) >= parseISO(authReviewFromDate) ||
                                        '"To Date" cannot be before "From Date"'
                                    );
                                    } catch {
                                    return "Invalid date format";
                                    }
                                },
                                },
                            }}
                            />
                        </div>
                    </div>
                </>


                <>
                                {isRequestingAuthorization && (
                                  <div className="bg-gray-200 border-l-4 border-green-300 p-4 my-2">
                                    <ModalDatePicker<AuthReviewNeededInput>
                                      labelPosition="top"
                                      label={"Requested Extension Date"}
                                      disabled={externalLoading}
                                      placeholder="mm/dd/yyyy"
                                      field={"requestedExtensionDate"}
                                      required
                                    />

                                    <ModalTextAreaInput
                                      labelPosition="top"
                                      label={"Rationale/Reason for Delay"}
                                      disabled={externalLoading}
                                      placeholder="Please enter rationale or reason for delay (maximum 4000 characters)"
                                      field={"rationaleForDelay"}
                                      required
                                      rowsNum={4}
                                      maxLength={4000}
                                      shouldStopInputOnMaxLength={false}
                                      showCharacterCount
                                    />
                                    <ModalDatePicker<AuthReviewNeededInput>
                                        labelPosition="top"
                                        label={
                                            "If this is for an assessment authorization extension, please add the date of the first scheduled assessment appointment"
                                        }
                                        disabled={externalLoading}
                                        placeholder="mm/dd/yyyy"
                                        field={"firstScheduledAssessment"}
                                        required={false}
                                    />

                                  </div>
                                )}

                </>

                <ModalTextAreaInput
                  labelPosition="top"
                  label={"Comments"}
                  disabled={externalLoading}
                  field={"comments"}
                  placeholder="Enter any additional comments"
                  required
                  rowsNum={3}
                  maxLength={4000}
                  shouldStopInputOnMaxLength={false}
                  showCharacterCount
                />
              </div>
            </div>

            <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
              <div className="flex w-1/4">
                <FatOutlineButton
                  color={"red"}
                  text={"Cancel"}
                  onClick={onCancel}
                />
                <span style={{ width: 10 }} />
                <FatOutlineButton type="submit" text={"Submit"} color={"green"} />
              </div>
            </div>
          </>
        )}

        {externalLoading && (
          <div className="p-24">
            <LoadingSpinner text="Processing" />
          </div>
        )}
      </form>
    </FormProvider>
  );
}
